import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { 
  Car, Calendar, Clock, CheckCircle, 
  AlertCircle, XCircle, DollarSign, User
} from 'lucide-react';
import { toast } from 'sonner';
import { HostAppShell } from '../components/HostAppShell';
import { getVehiclePrimaryImage } from '../lib/imageUtils';
import { getHostListBadge, normalizeHostStatus, isHostActionRequired } from '../lib/hostBookingStatus';

const HostBookings = () => {
  const { user, isHost, api } = useAuth();
  const navigate = useNavigate();
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('all');

  useEffect(() => {
    fetchHostBookings();
  }, []);

  const fetchHostBookings = async () => {
    try {
      // Use existing endpoint with role=owner to get host bookings
      const res = await api.get('/bookings?role=owner');
      setBookings(res.data.bookings || []);
    } catch (error) {
      console.error('Error fetching host bookings:', error);
      toast.error('Failed to load bookings');
    } finally {
      setLoading(false);
    }
  };

  // Filter bookings by status (using normalized statuses)
  const getFilteredBookings = () => {
    if (activeTab === 'all') return bookings;
    if (activeTab === 'requests') return bookings.filter(b => normalizeHostStatus(b.status) === 'requested');
    if (activeTab === 'active') return bookings.filter(b => 
      ['approved_pending_payment', 'paid', 'prehire_in_progress', 'prehire_submitted', 'active', 'trip_active', 'posthire_in_progress'].includes(normalizeHostStatus(b.status))
    );
    if (activeTab === 'completed') return bookings.filter(b => 
      ['posthire_submitted', 'cooldown_window_open', 'completed'].includes(normalizeHostStatus(b.status))
    );
    if (activeTab === 'cancelled') return bookings.filter(b => 
      ['cancelled', 'declined', 'expired', 'approval_expired'].includes(normalizeHostStatus(b.status))
    );
    return bookings;
  };

  // Get status badge styling - USE UNIFIED HOST STATUS SYSTEM
  const getStatusBadge = (status) => {
    return getHostListBadge(status);
  };

  const filteredBookings = getFilteredBookings();

  // Count badges (using normalized statuses)
  const requestsCount = bookings.filter(b => normalizeHostStatus(b.status) === 'requested').length;
  const activeCount = bookings.filter(b => 
    ['approved_pending_payment', 'paid', 'prehire_in_progress', 'prehire_submitted', 'active', 'trip_active', 'posthire_in_progress'].includes(normalizeHostStatus(b.status))
  ).length;

  if (!isHost()) {
    return (
      <HostAppShell title="Bookings" activeId="bookings">
        <Card className="max-w-md mx-auto">
          <CardContent className="p-6 text-center">
            <AlertCircle className="w-12 h-12 text-amber-500 mx-auto mb-4" />
            <h2 className="text-lg font-semibold mb-2">Host Access Required</h2>
            <p className="text-slate-500 mb-4">You need to be an approved host to view this page.</p>
            <Button onClick={() => navigate('/host')}>Go to Dashboard</Button>
          </CardContent>
        </Card>
      </HostAppShell>
    );
  }

  return (
    <HostAppShell title="Bookings" activeId="bookings">
      <div className="max-w-4xl mx-auto">
        {/* Tabs */}
        <div className="flex gap-2 overflow-x-auto pb-4 mb-4" data-testid="host-bookings-tabs">
          <Button 
            variant={activeTab === 'all' ? 'default' : 'outline'} 
            size="sm"
            onClick={() => setActiveTab('all')}
          >
            All ({bookings.length})
          </Button>
          <Button 
            variant={activeTab === 'requests' ? 'default' : 'outline'} 
            size="sm"
            onClick={() => setActiveTab('requests')}
            className="relative"
          >
            Requests
            {requestsCount > 0 && (
              <span className="ml-1.5 px-1.5 py-0.5 text-xs bg-red-500 text-white rounded-full">
                {requestsCount}
              </span>
            )}
          </Button>
          <Button 
            variant={activeTab === 'active' ? 'default' : 'outline'} 
            size="sm"
            onClick={() => setActiveTab('active')}
          >
            Active ({activeCount})
          </Button>
          <Button 
            variant={activeTab === 'completed' ? 'default' : 'outline'} 
            size="sm"
            onClick={() => setActiveTab('completed')}
          >
            Completed
          </Button>
          <Button 
            variant={activeTab === 'cancelled' ? 'default' : 'outline'} 
            size="sm"
            onClick={() => setActiveTab('cancelled')}
          >
            Cancelled
          </Button>
        </div>

        {/* Bookings List */}
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          </div>
        ) : filteredBookings.length === 0 ? (
          <Card className="border-slate-100">
            <CardContent className="p-8 text-center">
              <Calendar className="w-12 h-12 text-slate-300 mx-auto mb-4" />
              <h3 className="font-medium text-slate-700 mb-2">No bookings found</h3>
              <p className="text-sm text-slate-500">
                {activeTab === 'requests' 
                  ? 'No pending booking requests at the moment.'
                  : activeTab === 'active'
                  ? 'No active bookings right now.'
                  : 'Bookings for your vehicles will appear here.'}
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-3">
            {filteredBookings.map((booking) => {
              const badge = getStatusBadge(booking.status);
              return (
                <Link 
                  key={booking.booking_id} 
                  to={`/bookings/${booking.booking_id}`}
                  data-testid={`booking-item-${booking.booking_id}`}
                >
                  <Card className="border-slate-100 hover:shadow-md transition-all cursor-pointer">
                    <CardContent className="p-4">
                      <div className="flex items-start gap-4">
                        {/* Vehicle Thumbnail */}
                        <div className="w-16 h-16 rounded-lg bg-slate-200 flex items-center justify-center flex-shrink-0 overflow-hidden">
                          {getVehiclePrimaryImage(booking.vehicle) ? (
                            <img 
                              src={getVehiclePrimaryImage(booking.vehicle)}
                              alt={`${booking.vehicle?.make || ''} ${booking.vehicle?.model || ''}`}
                              className="w-full h-full object-cover"
                              onError={(e) => {
                                // On error, hide image and show fallback icon
                                e.target.style.display = 'none';
                                e.target.parentElement.innerHTML = '<svg class="w-8 h-8 text-slate-400" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" /></svg>';
                              }}
                            />
                          ) : (
                            <Car className="w-8 h-8 text-slate-400" />
                          )}
                        </div>

                        {/* Booking Info */}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-start justify-between gap-2">
                            <div>
                              <p className="font-medium text-slate-900 truncate">
                                {booking.vehicle?.make || 'Vehicle'} {booking.vehicle?.model || ''}
                              </p>
                              <div className="flex items-center gap-1 text-sm text-slate-500 mt-0.5">
                                <User className="w-3.5 h-3.5" />
                                <span className="truncate">{booking.customer?.full_name || 'Customer'}</span>
                              </div>
                            </div>
                            <Badge className={`text-xs border flex-shrink-0 ${badge.className}`}>
                              {badge.label}
                            </Badge>
                          </div>

                          <div className="flex items-center gap-4 mt-2 text-sm text-slate-600">
                            <div className="flex items-center gap-1">
                              <Calendar className="w-3.5 h-3.5" />
                              <span>{booking.start_date}</span>
                            </div>
                            {booking.subtotal && (
                              <div className="flex items-center gap-1">
                                <DollarSign className="w-3.5 h-3.5" />
                                <span>${booking.subtotal}</span>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </Link>
              );
            })}
          </div>
        )}
      </div>
    </HostAppShell>
  );
};

export default HostBookings;
